import { Component } from '@angular/core';
import { ActivatedRoute, RouterModule, RouterOutlet } from '@angular/router';
import { switchMap, catchError, of, startWith, shareReplay, map, combineLatest, Observable, BehaviorSubject } from 'rxjs';
import { WorldNavCountsService, WorldNavCountsResponse } from '../../../api/world-nav-counts.service';
import { WorldsService } from '../../../api/worlds.service';
import { WORLD_NAV_GROUPS } from '../../../consts/nav-group.const';
import { CATEGORY_ACCENTS } from '../../../consts/category-accent.const';
import { NavCategory } from '../../../types/nav-category.type';
import { NavLink } from '../../../types/nav-link.type';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-world-nav',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './world-nav.component.html',
  styleUrl: './world-nav.component.scss'
})
export class WorldNavComponent {
  navGroups = WORLD_NAV_GROUPS;
  collapsed = false;
  navId = `world-nav-${Math.random().toString(36).slice(2)}`;
  private readonly navCollapseKey = 'of.worldNav.collapsed';
  worldId = '';
  worldName: string | null = null;
  vm$: Observable<{ counts: WorldNavCountsResponse | null; worldName: string | null }>;
  private readonly navGroupStateKey = 'of.worldNav.groups';
  collapsedGroups: Record<string, boolean> = {};

  private readonly countsRefresh$ = new BehaviorSubject<void>(undefined);

  constructor(
    route: ActivatedRoute,
    private readonly counts: WorldNavCountsService,
    private readonly worlds: WorldsService
  ) {
    this.worldId = route.snapshot.paramMap.get('worldId') ?? '';
    this.restoreCollapsedState();
    this.restoreGroupState();

    const counts$ = this.countsRefresh$.pipe(
      switchMap(() =>
        this.counts.get(this.worldId).pipe(
          catchError(() => of(null as WorldNavCountsResponse | null)),
          startWith(null as WorldNavCountsResponse | null)
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const worldName$ = this.worlds
      .get(this.worldId)
      .pipe(
        map((res) => res.world.name),
        catchError(() => of(null as string | null)),
        startWith(null as string | null),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.vm$ = combineLatest([counts$, worldName$]).pipe(
      map(([counts, name]) => {
        if (name) {
          this.worldName = name;
        }
        return { counts, worldName: name ?? this.worldName };
      })
    );
  }

  private restoreCollapsedState() {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(`${this.navCollapseKey}:${this.worldId}`);
    if (stored !== null) {
      this.collapsed = stored === 'true';
      return;
    }
    this.collapsed = window.matchMedia('(max-width: 960px)').matches;
  }

  toggleSidebar() {
    this.collapsed = !this.collapsed;
    this.persistCollapsedState();
  }

  private persistCollapsedState() {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(`${this.navCollapseKey}:${this.worldId}`, this.collapsed ? 'true' : 'false');
  }

  private restoreGroupState() {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(`${this.navGroupStateKey}:${this.worldId}`);
    let parsed: Record<string, boolean> = {};
    if (stored) {
      try {
        parsed = JSON.parse(stored) as Record<string, boolean>;
      } catch {
        parsed = {};
      }
    }
    const next: Record<string, boolean> = { ...parsed };
    for (const group of this.navGroups) {
      if (next[group.label] === undefined) {
        next[group.label] = true;
      }
    }
    this.collapsedGroups = next;
  }

  toggleGroup(label: string) {
    this.collapsedGroups = {
      ...this.collapsedGroups,
      [label]: !this.isGroupCollapsed(label),
    };
    this.persistGroupState();
  }

  isGroupCollapsed(label: string) {
    const value = this.collapsedGroups[label];
    return value === undefined ? true : value;
  }

  private persistGroupState() {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      `${this.navGroupStateKey}:${this.worldId}`,
      JSON.stringify(this.collapsedGroups)
    );
  }

  accentFor(category: NavCategory, shade = 0) {
    const palette = CATEGORY_ACCENTS[category] ?? ['#00e5ff'];
    return palette[shade] ?? palette[0] ?? '#00e5ff';
  }

  groupCount(counts: WorldNavCountsResponse | null, keys?: Array<NavLink['countKey']>) {
    if (!keys || keys.length === 0) return 0;
    return keys.reduce<number>((total, key) => total + this.linkCount(counts, key), 0);
  }

  linkCount(counts: WorldNavCountsResponse | null, key?: NavLink['countKey']) {
    if (!key) return 0;
    if (key === 'world') return 1;
    return counts?.[key] ?? 0;
  }

  linkTarget(slug: string) {
    return slug
      ? ['/workspace/worlds', this.worldId, slug]
      : ['/workspace/worlds', this.worldId];
  }
}
